import { cva, type VariantProps } from "class-variance-authority";
import {
  ComponentProps,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
} from "react";

const textareaVariants = cva(
  [
    "placeholder:text-muted-foreground",
    "aria-invalid:ring-destructive/20",
    "dark:aria-invalid:ring-destructive/40",
    "aria-invalid:border-destructive",
    "dark:bg-input/30",
    "flex",
    "field-sizing-content",
    "min-h-16",
    "w-full",
    "rounded-md",
    "bg-transparent",
    "px-3",
    "py-2",
    "text-base",
    "transition-[color,box-shadow]",
    "outline-none",
    "disabled:cursor-not-allowed",
    "disabled:opacity-50",
    "md:text-sm",
  ],
  {
    variants: {
      border: {
        default: [
          "border",
          "border-input",
          "shadow-xs",
          "focus-visible:border-ring",
          "focus-visible:ring-ring/50",
          "focus-visible:ring-[3px]",
        ],
        faint: [
          "border",
          "border-neutral-100",
          "focus-visible:border-ring",
          "focus-visible:ring-ring/50",
          "focus-visible:ring-[2px]",
        ],
        none: [
          "border-none",
          "shadow-none",
          "focus:ring-0",
          "focus:outline-none",
        ],
      },
    },
    defaultVariants: {
      border: "default",
    },
  },
);

export interface TextareaProps
  extends ComponentProps<"textarea">, VariantProps<typeof textareaVariants> {
  autoHeight?: boolean;
}

export const Textarea = forwardRef(function Textarea(
  { className, border, autoHeight, value, onInput, ...props }: TextareaProps,
  ref: React.ForwardedRef<HTMLTextAreaElement>,
) {
  const internalRef = useRef<HTMLTextAreaElement | null>(null);
  const updateSize = useCallback(() => {
    const textArea = internalRef.current;

    if (textArea) {
      textArea.style.height = autoHeight ? `${textArea.scrollHeight}px` : "";
    }
  }, [autoHeight]);
  const setRef = useCallback(
    (textArea: HTMLTextAreaElement | null) => {
      if (typeof ref === "function") {
        ref(textArea);
      } else if (ref) {
        ref.current = textArea;
      }

      internalRef.current = textArea;
    },
    [ref],
  );

  useEffect(() => updateSize, [updateSize, value, autoHeight]);

  return (
    <textarea
      ref={setRef}
      data-slot="textarea"
      onInput={(event) => {
        updateSize();

        if (onInput) {
          onInput(event);
        }
      }}
      className={textareaVariants({ border, className })}
      value={value}
      {...props}
    />
  );
});
